/* global React, Button, Icon, toast, t */

const BUSINESS_INVITE_KEY = 'plumme-pending-business-invite';

const PlummeBusinessInvite = {
  capture() {
    const params = new URLSearchParams(window.location.search || '');
    const inviteId = String(params.get('businessInvite') || '').trim();
    if (inviteId) {
      localStorage.setItem(BUSINESS_INVITE_KEY, inviteId);
      params.delete('businessInvite');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`);
    }
    return inviteId || localStorage.getItem(BUSINESS_INVITE_KEY) || '';
  },
  current() { return localStorage.getItem(BUSINESS_INVITE_KEY) || ''; },
  clear(inviteId) {
    if (!inviteId || this.current() === inviteId) localStorage.removeItem(BUSINESS_INVITE_KEY);
  },
};

PlummeBusinessInvite.capture();

const accountantRoleLabel = (role) => ({ owner: t('biv.role.owner'), accountant: t('biv.role.accountant'), admin: t('biv.role.admin'), viewer: t('biv.role.viewer') }[role] || t('biv.role.fallback'));
const isOwnershipTransferInvite = (invite) => invite?.type === 'ownership_transfer' || invite?.role === 'owner';

function AccountantOnboardingDialog({ inviteId, onClose }) {
  const backend = window.PlummeBackend.useBackend();
  const [step, setStep] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const invite = (backend.incomingInvites || []).find((entry) => entry.id === inviteId)
    || (backend.incomingInvites || []).find((entry) => entry.status === 'pending');
  const expired = invite?.status === 'expired';
  const ownershipTransfer = isOwnershipTransferInvite(invite);

  // Backend krever bekreftet e-post for en invitasjon kan gi tilgang. Uten en vei
  // videre her ville brukeren bare fatt en feilmelding og statt fast.
  const [needsEmailVerification, setNeedsEmailVerification] = React.useState(false);

  const accept = async () => {
    if (!invite || expired) return;
    setBusy(true);
    try {
      const membership = await window.PlummeBackend.acceptBusinessInvite(invite.id);
      PlummeBusinessInvite.clear(invite.id);
      setNeedsEmailVerification(false);
      window.dispatchEvent(new CustomEvent('plumme-business-invite-accepted', { detail: membership || {} }));
      toast(ownershipTransfer ? t('biv.toast.nowOwner', { name: membership?.businessName || invite.businessName }) : t('biv.toast.gotAccess', { name: membership?.businessName || invite.businessName }), 'success');
      onClose();
    } catch (error) {
      if (error?.domainCode === 'auth.emailNotVerified') {
        setNeedsEmailVerification(true);
        await window.PlummeBackend.resendEmailVerification?.().catch(() => {});
      }
      toast(error?.message || t('biv.toast.acceptFailed'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const recheckEmailVerification = async () => {
    setBusy(true);
    try {
      const verified = await window.PlummeBackend.reloadEmailVerification?.();
      if (verified) {
        setNeedsEmailVerification(false);
        await accept();
        return;
      }
      toast(t('biv.toast.stillNotVerified'), 'error');
    } finally {
      setBusy(false);
    }
  };

  // Uten en ventende invitasjon er dette inngangen for regnskapsføreren: enten
  // jobber hen i et byrå, eller alene med enkeltklienter. Byråveien lå tidligere bare
  // bak adressen /byra, og var i praksis umulig å finne.
  if (inviteId && !invite) return <div className="ob-scrim"><div className="ob-card ob-card-wide" role="dialog" aria-label={t('biv.clientInvitation')}>
    <button className="ob-close" onClick={onClose} aria-label={t('sharedText.ui.close')}><Icon name="x" size={18} /></button>
    <div className="ob-stage"><div className="ob-hero">
      <img className="ob-hero-plum" src="src/img/SuperPlumma.png" alt="" />
      <div className="ob-eyebrow">{t('biv.clientInvitation')}</div>
      <h2 className="ob-title">{t('biv.signInWithInviteEmail')}</h2>
      <p className="ob-lead">{t('biv.noActiveInvite', { email: backend.user?.email || t('biv.thisAccount') })}</p>
      <p className="ob-lead">{t('biv.signOutAndIn')}</p>
    </div></div>
    <div className="ob-nav">
      <button className="ob-skip" onClick={onClose}>{t('biv.later')}</button>
      <Button icon="arrowUpRight" onClick={() => window.PlummeBackend?.signOut?.()}>{t('sharedText.pay.signOut')}</Button>
    </div>
  </div></div>;
  if (!invite) return <div className="ob-scrim"><div className="ob-card ob-card-wide" role="dialog" aria-label={t('biv.accountantSetup')}>
    <button className="ob-close" onClick={onClose} aria-label={t('sharedText.ui.close')}><Icon name="x" size={18} /></button>
    <div className="ob-stage"><div className="ob-hero">
      <img className="ob-hero-plum" src="src/img/SuperPlumma.png" alt="" />
      <div className="ob-eyebrow">{t('sharedText.rf.group.firm')}</div>
      <h2 className="ob-title">{t('biv.workWithClients')}</h2>
      <p className="ob-lead">{t('biv.firmIntro')}</p>
      <p className="ob-lead">{t('biv.soloIntro')}</p>
    </div></div>
    <div className="ob-nav">
      <button className="ob-skip" onClick={onClose}>{t('sharedText.ui.close')}</button>
      <Button variant="secondary" icon="building" onClick={() => { onClose(); window.setTimeout(() => window.showOnboarding?.('foretak'), 0); }}>{t('biv.createClientBusiness')}</Button>
      <Button icon="users" onClick={() => { onClose(); window.setTimeout(() => window.showOnboarding?.('byra'), 0); }}>{t('sharedText.rf.create.title')}</Button>
    </div>
  </div></div>;

  const stages = [
    <div className="ob-hero" key="welcome">
      <img className="ob-hero-plum" src="src/img/SuperPlumma.png" alt="" />
      <div className="ob-eyebrow">{ownershipTransfer ? t('biv.ownershipChange') : t('biv.accountantClientAccess')}</div>
      <h2 className="ob-title">{ownershipTransfer ? t('biv.takeOver', { name: invite.businessName }) : t('biv.invitedTo', { name: invite.businessName })}</h2>
      <p className="ob-lead">{ownershipTransfer ? t('biv.leadOwnership') : t('biv.leadRole', { role: accountantRoleLabel(invite.role).toLowerCase() })}</p>
    </div>,
    <div key="access">
      <div className="ob-eyebrow">{t('biv.step1')}</div>
      <h2 className="ob-title">{t('biv.whatRoleCanDo')}</h2>
      <div className="ob-fields">
        <div className="ob-warning"><Icon name="check" size={17} /><span>{t('biv.canWork')}</span></div>
        <div className="ob-warning"><Icon name="lock" size={17} /><span>{ownershipTransfer ? t('biv.cannotMoveOwner') : t('biv.cannotOwn')}</span></div>
        <div className="ob-warning"><Icon name="users" size={17} /><span>{t('biv.dataSeparate')}</span></div>
      </div>
    </div>,
    <div className="ob-hero" key="ready">
      <img className="ob-hero-plum" src="src/img/PlummaCheer.png" alt="" />
      <div className="ob-eyebrow">{t('biv.step2')}</div>
      <h2 className="ob-title">{t('sharedText.ui.open')} {invite.businessName}</h2>
      <p className="ob-lead">{ownershipTransfer ? t('biv.readyOwnership') : t('biv.readyAccess')}</p>
      {expired && <div className="ob-warning ob-warning-error"><Icon name="alertTri" size={17} /><span>{t('biv.expired')}</span></div>}
      {needsEmailVerification && <div className="ob-warning ob-warning-error">
        <Icon name="alertTri" size={17} />
        <span>{t('biv.verifyEmail', { email: backend.user?.email || t('biv.yourAddress') })}</span>
      </div>}
    </div>,
  ];

  return <div className="ob-scrim" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="ob-card" role="dialog" aria-label={t('biv.accountantInvitation')}>
      <button className="ob-close" onClick={onClose} aria-label={t('sharedText.ui.close')}><Icon name="x" size={18} /></button>
      <div className="ob-progress">{stages.map((_, index) => <span key={index} className={`ob-dot${index === step ? ' on' : index < step ? ' done' : ''}`} />)}</div>
      <div className="ob-stage">{stages[step]}</div>
      <div className="ob-nav">
        {step > 0 ? <button className="ob-skip" onClick={() => setStep((value) => value - 1)}>{t('biv.back')}</button> : <button className="ob-skip" onClick={onClose}>{t('biv.later')}</button>}
        {step < stages.length - 1
          ? <Button icon="arrowRight" onClick={() => setStep((value) => value + 1)} style={{ marginLeft: 'auto' }}>{t('sharedText.buy.next')}</Button>
          : needsEmailVerification
            ? <Button icon="check" disabled={busy} onClick={recheckEmailVerification} style={{ marginLeft: 'auto' }}>{busy ? t('biv.checking') : t('biv.iHaveVerified')}</Button>
            : <Button icon="check" disabled={busy || expired} onClick={accept} style={{ marginLeft: 'auto' }}>{busy ? t('biv.accepting') : t('biv.acceptAndOpen')}</Button>}
      </div>
    </div>
  </div>;
}

function BusinessInviteGate({ backend }) {
  const pendingId = PlummeBusinessInvite.current();
  const invites = backend?.incomingInvites || [];
  const invite = invites.find((entry) => entry.id === pendingId) || invites.find((entry) => entry.status === 'pending') || invites[0];
  const [busy, setBusy] = React.useState(false);
  if (!backend?.user) return null;
  if (!invite && pendingId) return <aside className="household-invite-global" role="status">
    <span className="household-invite-global__icon"><Icon name="building" size={20} /></span>
    <div className="grow"><div className="t-h3">{t('biv.pendingTitle')}</div><div className="t-sm muted">{t('biv.pendingText')}</div></div>
    <div className="row gap-8"><Button variant="ghost" size="sm" disabled={busy} onClick={() => PlummeBusinessInvite.clear(pendingId)}>{t('sharedText.adv.docs.hide')}</Button><Button size="sm" onClick={() => window.showOnboarding?.('regnskapsforer', { inviteId: pendingId })}>{t('biv.viewInvitation')}</Button></div>
  </aside>;
  if (!invite) return null;
  const expired = invite.status === 'expired';
  const decline = async () => {
    setBusy(true);
    try {
      await window.PlummeBackend.declineBusinessInvite(invite.id);
      PlummeBusinessInvite.clear(invite.id);
      toast(t('biv.toast.declined'), 'success');
    } catch (error) {
      toast(error?.message || t('biv.toast.declineFailed'), 'error');
    } finally { setBusy(false); }
  };
  return <aside className="household-invite-global" role="status">
    <span className="household-invite-global__icon"><Icon name="building" size={20} /></span>
    <div className="grow"><div className="t-h3">{expired ? (isOwnershipTransferInvite(invite) ? t('biv.expiredOwnership') : t('biv.expiredInvitation')) : (isOwnershipTransferInvite(invite) ? t('biv.newOwnership') : t('biv.newInvitation'))}</div><div className="t-sm muted">{invite.businessName} · {accountantRoleLabel(invite.role)}{expired ? ' · ' + t('biv.askSenderResend') : ''}</div></div>
    <div className="row gap-8"><Button variant="ghost" size="sm" disabled={busy} onClick={decline}>{t('sharedText.fe.decline')}</Button><Button size="sm" disabled={expired} onClick={() => window.showOnboarding?.('regnskapsforer', { inviteId: invite.id })}>{t('biv.viewAccess')}</Button></div>
  </aside>;
}

Object.assign(window, { AccountantOnboardingDialog, BusinessInviteGate, PlummeBusinessInvite });
